import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CalendarIcon, Plus, Droplets, Target, TrendingUp, Award, Edit3, Info, Sunrise, Sun, Moon, Apple, Flame, Beef, Wheat, Droplet, BookOpen, Camera, Upload, PenLine, Sparkles } from 'lucide-react';
import { useComprehensiveNutrition } from '@/contexts/NutritionContext';
import { FoodEntry, Food } from '@/hooks/useComprehensiveNutrition';
import { FoodSearchModal } from './FoodSearchModal';
import { EditFoodEntryModal } from './EditFoodEntryModal';
import { PhotoScanModal } from './PhotoScanModal';
import { WriteFoodModal } from './WriteFoodModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DetectedFoodReviewModal } from './DetectedFoodReviewModal';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { savedMealItemToFood } from '@/lib/savedMealFood';
import { VitalStatsCard } from './VitalStatsCard';
import { NutritionTrendsChart } from './NutritionTrendsChart';
import { CalorieSurplusDeficitChart } from './CalorieSurplusDeficitChart';
import { CircularProgress } from './CircularProgress';
import { NutritionIcons } from './NutritionIcons';
import { useSavedMeals } from '@/hooks/useSavedMeals';
import { useProfile } from '@/hooks/useProfile';
import { Analytics } from '@/lib/analytics';

export const NutritionDashboard = () => {
  const { t } = useTranslation();

  const mealTypes = useMemo(() => ([
    { key: 'breakfast' as const, name: t('components.nutritionDashboard.meals.breakfast'), Icon: Sunrise },
    { key: 'lunch' as const, name: t('components.nutritionDashboard.meals.lunch'), Icon: Sun },
    { key: 'dinner' as const, name: t('components.nutritionDashboard.meals.dinner'), Icon: Moon },
    { key: 'snack' as const, name: t('components.nutritionDashboard.meals.snacks'), Icon: Apple },
  ]), [t]);

  const nutritionHookData = useComprehensiveNutrition();
  const { toast } = useToast();
  const { savedMeals, loading: savedMealsLoading } = useSavedMeals();
  const { profile } = useProfile();
  const [foodSearchModal, setFoodSearchModal] = useState<{
    isOpen: boolean;
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  }>({ isOpen: false, mealType: 'breakfast' });
  const [importMealDialog, setImportMealDialog] = useState<{
    isOpen: boolean;
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  }>({ isOpen: false, mealType: 'breakfast' });
  const [photoScanModal, setPhotoScanModal] = useState<{
    isOpen: boolean;
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
    initialSource?: 'camera' | 'photos';
  }>({ isOpen: false, mealType: 'breakfast' });
  const [detectedFoodsModal, setDetectedFoodsModal] = useState<{
    isOpen: boolean;
    foods: any[];
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  }>({ isOpen: false, foods: [], mealType: 'breakfast' });
  const [writeFoodModal, setWriteFoodModal] = useState<{
    isOpen: boolean;
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  }>({ isOpen: false, mealType: 'breakfast' });
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    entry: FoodEntry | null;
  }>({ isOpen: false, entry: null });
  const [waterAmount, setWaterAmount] = useState(0.25);
  const [editingCalorieGoal, setEditingCalorieGoal] = useState(false);
  const [editingProteinGoal, setEditingProteinGoal] = useState(false);
  const [editingCarbGoal, setEditingCarbGoal] = useState(false);
  const [editingFatGoal, setEditingFatGoal] = useState(false);
  const [tempCalorieGoal, setTempCalorieGoal] = useState<string>('');
  const [tempProteinGoal, setTempProteinGoal] = useState<string>('');
  const [tempCarbGoal, setTempCarbGoal] = useState<string>('');
  const [tempFatGoal, setTempFatGoal] = useState<string>('');

  const {
    nutritionGoals,
    dailyNutrition,
    selectedDate,
    setSelectedDate,
    logFoodToMeal,
    logWaterIntake,
    deleteFoodEntry,
    updateFoodEntry,
    calculateCalorieNeeds,
    updateNutritionGoals
  } = nutritionHookData;

  const handleSaveCalorieGoal = async () => {
    const value = parseInt(tempCalorieGoal, 10);
    if (!isNaN(value) && value > 0) {
      try {
        await updateNutritionGoals({ daily_calories: value });
        toast({ title: "Calorie goal updated", description: `New goal: ${value} cal. This will persist until you change it.` });
      } catch (error) {
        toast({ title: "Failed to update", description: "Please try again", variant: "destructive" });
      }
    }
    setEditingCalorieGoal(false);
  };

  const handleSaveProteinGoal = async () => {
    const value = parseInt(tempProteinGoal, 10);
    if (!isNaN(value) && value > 0) {
      try {
        await updateNutritionGoals({ daily_protein: value });
        toast({ title: "Protein goal updated", description: `New goal: ${value}g. This will persist until you change it.` });
      } catch (error) {
        toast({ title: "Failed to update", description: "Please try again", variant: "destructive" });
      }
    }
    setEditingProteinGoal(false);
  };

  const handleSaveCarbGoal = async () => {
    const value = parseInt(tempCarbGoal, 10);
    if (!isNaN(value) && value > 0) {
      try {
        await updateNutritionGoals({ daily_carbs: value });
        toast({ title: "Carb goal updated", description: `New goal: ${value}g. This will persist until you change it.` });
      } catch (error) {
        toast({ title: "Failed to update", description: "Please try again", variant: "destructive" });
      }
    }
    setEditingCarbGoal(false);
  };

  const handleSaveFatGoal = async () => {
    const value = parseInt(tempFatGoal, 10);
    if (!isNaN(value) && value > 0) {
      try {
        await updateNutritionGoals({ daily_fat: value });
        toast({ title: "Fat goal updated", description: `New goal: ${value}g. This will persist until you change it.` });
      } catch (error) {
        toast({ title: "Failed to update", description: "Please try again", variant: "destructive" });
      }
    }
    setEditingFatGoal(false);
  };

  // Base BMR/TDEE calculation from the hook (uses current profile data)
  const { tdee: baseTdee, bmr } = calculateCalorieNeeds();

  // For display in the TDEE explanation, always use the same
  // activity level that is stored with the active nutrition goals
  // (falling back to the profile value if goals are missing).
  const activityLevelForDisplay = (nutritionGoals?.activity_level || profile?.activity_level || 'moderate') as
    | 'sedentary'
    | 'light'
    | 'moderate'
    | 'active'
    | 'very_active';

  const activityMultipliersForDisplay: Record<string, number> = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    very_active: 1.725,
    extra_active: 1.9,
  };

  // Map "active" to "very_active" to stay consistent with the backend logic
  const mappedActivityLevelForDisplay =
    activityLevelForDisplay === 'active' ? 'very_active' : activityLevelForDisplay;

  const tdeeForDisplay = profile?.weight_kg && profile?.height_cm && profile?.birth_date
    ? Math.round(bmr * (activityMultipliersForDisplay[mappedActivityLevelForDisplay] || 1.55))
    : baseTdee;


  const handleAddFood = async (food: any, quantity: number, unit: string) => {
    console.log('NutritionDashboard: handleAddFood called with:', { food: food.name, quantity, unit, mealType: foodSearchModal.mealType });
    try {
      const result = await logFoodToMeal(foodSearchModal.mealType, food, quantity, unit, selectedDate);
      console.log('NutritionDashboard: Food logged successfully:', result);
      
      // Track analytics (non-blocking - don't let analytics errors prevent food from being added)
      try {
        Analytics.mealLogged(foodSearchModal.mealType || 'snack', {
          food_name: food.name,
          quantity,
          unit,
          calories: food.calories_per_serving * (unit === 'serving' ? quantity : quantity / food.serving_size)
        });
      } catch (analyticsError) {
        console.warn('NutritionDashboard: Analytics error (non-critical):', analyticsError);
        // Don't throw - food was successfully added
      }
      
      toast({
        title: "Food logged",
        description: `${food.name} added to ${foodSearchModal.mealType}.`
      });
      setFoodSearchModal({ ...foodSearchModal, isOpen: false });
    } catch (error) {
      console.error('NutritionDashboard: Error logging food:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to log food.",
        variant: "destructive"
      });
    }
  };

  const handleLogWater = async () => {
    try {
      await logWaterIntake(waterAmount, selectedDate);
      
      // Track analytics (non-blocking)
      try {
        Analytics.waterLogged(waterAmount);
      } catch (analyticsError) {
        console.warn('NutritionDashboard: Analytics error (non-critical):', analyticsError);
      }
      
      toast({
        title: "Water logged",
        description: `${waterAmount}L of water added.`
      });
      setWaterAmount(0.25);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log water.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    try {
      await deleteFoodEntry(entryId);
      
      // Track analytics (non-blocking)
      try {
        Analytics.mealDeleted();
      } catch (analyticsError) {
        console.warn('NutritionDashboard: Analytics error (non-critical):', analyticsError);
      }
      
      toast({
        title: "Food removed",
        description: "Food entry has been deleted."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete food entry.",
        variant: "destructive"
      });
    }
  };

  const handleEditEntry = (entry: FoodEntry) => {
    setEditModal({ isOpen: true, entry });
  };

  const handleSaveEdit = async (entryId: string, quantity: number, nutritionData: any) => {
    try {
      await updateFoodEntry(entryId, {
        quantity,
        ...nutritionData
      });
      toast({
        title: "Food updated",
        description: "Food entry has been updated successfully."
      });
      setEditModal({ isOpen: false, entry: null });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update food entry.",
        variant: "destructive"
      });
    }
  };

  const handleImportSavedMeal = async (mealId: string) => {
    const savedMeal = savedMeals.find(m => m.id === mealId);
    if (!savedMeal) return;

    try {
      // Add all items from the saved meal to the current meal type
      for (const item of savedMeal.items ?? []) {
        await logFoodToMeal(importMealDialog.mealType, savedMealItemToFood(item), 1, 'serving', selectedDate);
      }
      
      toast({
        title: "Meal imported",
        description: `${savedMeal.name} has been added to ${importMealDialog.mealType}.`
      });
      setImportMealDialog({ ...importMealDialog, isOpen: false });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to import saved meal.",
        variant: "destructive"
      });
    }
  };

  const handlePhotoAnalysisComplete = (detectedFoods: any[], rawResponse: string) => {
    console.log('Photo analysis complete:', detectedFoods);
    setDetectedFoodsModal({
      isOpen: true,
      foods: detectedFoods,
      mealType: photoScanModal.mealType
    });
    setPhotoScanModal({ ...photoScanModal, isOpen: false });
  };

  const handleWriteFoodAnalysisComplete = (detectedFoods: any[], rawResponse: string) => {
    console.log('Text food analysis complete:', detectedFoods);
    setDetectedFoodsModal({
      isOpen: true,
      foods: detectedFoods,
      mealType: writeFoodModal.mealType
    });
    setWriteFoodModal({ ...writeFoodModal, isOpen: false });
  };

  const handleConfirmDetectedFoods = async (foods: any[]) => {
    try {
      for (const detectedFood of foods) {
        // All foods have AI-estimated nutrition already
        // Store per-gram values so editing in grams works correctly
        const portionGrams = detectedFood.portion_grams || 100;
        const food: Food = {
          id: crypto.randomUUID(),
          name: detectedFood.name,
          serving_size: 1, // 1g base
          serving_unit: 'g',
          calories_per_serving: detectedFood.calories / portionGrams,
          protein_per_serving: detectedFood.protein / portionGrams,
          carbs_per_serving: detectedFood.carbs / portionGrams,
          fat_per_serving: detectedFood.fat / portionGrams,
          fiber_per_serving: (detectedFood.fiber || 0) / portionGrams,
          sugar_per_serving: (detectedFood.sugar || 0) / portionGrams,
          sodium_per_serving: (detectedFood.sodium || 0) / portionGrams,
        };
        // Log with portion_grams as quantity and 'g' as unit
        await logFoodToMeal(detectedFoodsModal.mealType, food, portionGrams, 'g', selectedDate);
      }

      toast({
        title: "Foods logged",
        description: `${foods.length} item(s) added to ${detectedFoodsModal.mealType}.`
      });
      setDetectedFoodsModal({ isOpen: false, foods: [], mealType: 'breakfast' });
    } catch (error) {
      console.error('Error logging detected foods:', error);
      toast({
        title: "Error",
        description: "Failed to log some foods.",
        variant: "destructive"
      });
    }
  };

  const getProgressColor = (current: number, target: number) => {
    const percentage = (current / target) * 100;
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Safe percentage helper: prevents full bars when targets are 0/undefined
  const pct = (current: number, target?: number) => {
    const t = Number(target) || 0;
    if (t <= 0) return 0;
    const p = ((Number(current) || 0) / t) * 100;
    return Math.max(0, Math.min(p, 100));
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const selectedDateFormatted = format(new Date(selectedDate), "PPP");

  return (
    <div className="p-4 space-y-6 pb-32">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-normal text-foreground">{t('components.nutritionDashboard.pageTitle')}</h1>
            <p className="text-sm sm:text-base text-muted-foreground">{t('components.nutritionDashboard.pageSubtitle')}</p>
          </div>
          <div className="w-full sm:w-auto shrink-0">
            <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full sm:w-[200px] justify-start text-left font-normal",
                  !selectedDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                <span className="truncate">{selectedDate ? format(new Date(selectedDate), "PPP") : t('components.nutritionDashboard.pickDate')}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={new Date(selectedDate)}
                onSelect={(date) => {
                  if (date) {
                    // Format date to YYYY-MM-DD in local timezone to avoid timezone issues
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    setSelectedDate(`${year}-${month}-${day}`);
                  }
                }}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
                disabled={(date) => date > new Date()}
              />
            </PopoverContent>
          </Popover>
          </div>
        </div>

        {/* Overview Cards - Circular Progress */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-center gap-1">
                <h2 className="text-lg font-normal text-foreground">{t('components.nutritionDashboard.dailyGoals')}</h2>
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted"
                      aria-label={t('components.nutritionDashboard.targetsInfoAria')}
                    >
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{t('components.nutritionDashboard.targetsInfo.dialogTitle')}</DialogTitle>
                      <DialogDescription className="space-y-3 text-sm pt-2 text-left">
                        <p>{t('components.nutritionDashboard.targetsInfo.intro')}</p>
                        <div className="bg-muted/50 p-3 rounded-md space-y-2">
                          <p className="font-medium text-foreground">{t('components.nutritionDashboard.targetsInfo.suggestedTitle')}</p>
                          <p>{t('components.nutritionDashboard.targetsInfo.suggestedBody')}</p>
                        </div>
                        <div className="bg-muted/50 p-3 rounded-md space-y-2">
                          <p className="font-medium text-foreground">{t('components.nutritionDashboard.targetsInfo.manualTitle')}</p>
                          <p>{t('components.nutritionDashboard.targetsInfo.manualBody')}</p>
                        </div>
                        <p className="text-muted-foreground italic">
                          {nutritionGoals?.is_manual_override
                            ? t('components.nutritionDashboard.targetsInfo.currentModeManual')
                            : t('components.nutritionDashboard.targetsInfo.currentModeSuggested')}
                        </p>
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {nutritionGoals?.is_manual_override
                ? t('components.nutritionDashboard.targetsHintManual')
                : t('components.nutritionDashboard.targetsHintSuggested')}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              <CircularProgress
                value={dailyNutrition?.totals.calories || 0}
                max={nutritionGoals?.daily_calories || tdeeForDisplay}
                label={t('components.nutritionDashboard.ringCalories')}
                unit=" cal"
                icon={<Flame className="h-5 w-5" />}
                gradientId="calories-gradient"
                gradientColors={{ start: "#f97316", end: "#fb923c" }}
                glowColor="#f97316"
              />
              <CircularProgress
                value={dailyNutrition?.totals.protein || 0}
                max={nutritionGoals?.daily_protein || 120}
                label={t('components.nutritionDashboard.ringProtein')}
                unit="g"
                icon={<Beef className="h-5 w-5" />}
                gradientId="protein-gradient"
                gradientColors={{ start: "#ef4444", end: "#f87171" }}
                glowColor="#ef4444"
              />
              <CircularProgress
                value={dailyNutrition?.totals.carbs || 0}
                max={nutritionGoals?.daily_carbs || 250}
                label={t('components.nutritionDashboard.ringCarbs')}
                unit="g"
                icon={<Wheat className="h-5 w-5" />}
                gradientId="carbs-gradient"
                gradientColors={{ start: "#eab308", end: "#84cc16" }}
                glowColor="#84cc16"
              />
              <CircularProgress
                value={dailyNutrition?.totals.fat || 0}
                max={nutritionGoals?.daily_fat || 65}
                label={t('components.nutritionDashboard.ringFat')}
                unit="g"
                icon={<Droplet className="h-5 w-5" />}
                gradientId="fat-gradient"
                gradientColors={{ start: "#fdd835", end: "#fbc02d" }}
                glowColor="#fdd835"
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Meals */}
          <div className="lg:col-span-2">
            <Accordion type="multiple" defaultValue={[]} className="space-y-4">
              {mealTypes.map(({ key, name, Icon }) => {
                const mealEntries = dailyNutrition?.meals[key] || [];
                const mealTotals = mealEntries.reduce(
                  (acc, entry) => ({
                    calories: acc.calories + (entry.calories || 0),
                    protein: acc.protein + (entry.protein || 0),
                    carbs: acc.carbs + (entry.carbs || 0),
                    fat: acc.fat + (entry.fat || 0),
                  }),
                  { calories: 0, protein: 0, carbs: 0, fat: 0 }
                );

                return (
                  <Card key={key}>
                <AccordionItem value={key} className="border-none">
                      <CardHeader className="pb-4 sm:pb-5 px-3 sm:px-6">
                        <div className="space-y-3">
                          {/* Meal Title Row */}
                          <div className="flex items-center gap-2 sm:gap-3">
                            <Icon className="h-5 w-5 text-primary shrink-0" />
                            <span className="text-base sm:text-lg font-normal">{name}</span>
                          </div>
                          
                          {/* Nutrition Summary (if entries exist) */}
                          {mealEntries.length > 0 && (
                            <div className="pl-7 sm:pl-8">
                              <NutritionIcons
                                calories={mealTotals.calories}
                                protein={mealTotals.protein}
                                carbs={mealTotals.carbs}
                                fat={mealTotals.fat}
                                className="text-muted-foreground"
                              />
                            </div>
                          )}
                          
                          {/* Action Buttons Row */}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFoodSearchModal({ isOpen: true, mealType: key as any });
                              }}
                              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 flex-1"
                            >
                              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                              <span className="hidden sm:inline">{t('components.nutritionDashboard.addFood')}</span>
                              <span className="sm:hidden">{t('components.nutritionDashboard.addFood')}</span>
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={(e) => e.stopPropagation()}
                                  className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 flex-1"
                                >
                                  <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                                  <span className="hidden sm:inline">{t('components.nutritionDashboard.aiLog')}</span>
                                  <span className="sm:hidden">{t('components.nutritionDashboard.aiLog')}</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="center" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenuItem onClick={() => setPhotoScanModal({ isOpen: true, mealType: key as any, initialSource: 'camera' })}>
                                  <Camera className="h-4 w-4 mr-2" />
                                  {t('components.nutritionDashboard.aiLogCamera')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setPhotoScanModal({ isOpen: true, mealType: key as any, initialSource: 'photos' })}>
                                  <Upload className="h-4 w-4 mr-2" />
                                  {t('components.nutritionDashboard.aiLogUpload')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setWriteFoodModal({ isOpen: true, mealType: key as any })}>
                                  <PenLine className="h-4 w-4 mr-2" />
                                  {t('components.nutritionDashboard.aiLogWrite')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setImportMealDialog({ isOpen: true, mealType: key as any });
                              }}
                              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 flex-1"
                            >
                              <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                              <span className="hidden sm:inline">{t('components.nutritionDashboard.import')}</span>
                              <span className="sm:hidden">{t('components.nutritionDashboard.saved')}</span>
                            </Button>
                            <AccordionTrigger className="hover:no-underline p-2 h-9 w-9 flex items-center justify-center border rounded-md shrink-0">
                              <svg
                                className="h-4 w-4 text-muted-foreground transition-transform duration-200"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </AccordionTrigger>
                          </div>
                        </div>
                      </CardHeader>
                      <AccordionContent>
                        <CardContent className="pt-0">
                          {mealEntries.length ? (
                            <div className="space-y-2">
                              {mealEntries.map((entry) => (
                                <div key={entry.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                  <div className="flex-1">
                                    <h4 className="font-medium">
                                      {entry.food_name || entry.food?.name || entry.user_food?.name || t('components.nutritionDashboard.unknownFood')}
                                    </h4>
                                    <p className="text-sm text-muted-foreground">
                                      {entry.quantity} {entry.unit}
                                    </p>
                                    <NutritionIcons
                                      calories={entry.calories}
                                      protein={entry.protein}
                                      carbs={entry.carbs}
                                      fat={entry.fat}
                                      className="text-muted-foreground mt-1"
                                      iconSize={12}
                                    />
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleEditEntry(entry)}
                                      className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700"
                                    >
                                      <Edit3 className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteEntry(entry.id)}
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                    >
                                      ×
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted-foreground text-center py-4">
                              {t('components.nutritionDashboard.noFoodsForMeal', { meal: name.toLowerCase() })}
                            </p>
                          )}
                        </CardContent>
                      </AccordionContent>
                    </AccordionItem>
                  </Card>
                );
              })}
            </Accordion>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Water Intake */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-normal">
                  <Droplets className="h-5 w-5 text-blue-500" />
                  {t('components.nutritionDashboard.waterIntake')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span>{dailyNutrition?.water_intake?.toFixed(1) || 0}L</span>
                    <span className="text-muted-foreground">/ {nutritionGoals?.daily_water_liters || 2.5}L</span>
                  </div>
                  <Progress 
                    value={pct(dailyNutrition?.water_intake || 0, nutritionGoals?.daily_water_liters || 2.5)}
                    className="h-3 [&>div]:bg-blue-500"
                    style={{
                      filter: 'drop-shadow(0 0 8px #3b82f680) drop-shadow(0 0 4px #3b82f6)'
                    }}
                  />
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={waterAmount}
                      onChange={(e) => setWaterAmount(parseFloat(e.target.value) || 0.25)}
                      onFocus={(e) => e.target.select()}
                      className="w-20"
                    />
                    <Button onClick={handleLogWater} size="sm" className="flex-1">
                      {t('components.nutritionDashboard.logWater')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Daily Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-normal">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  {t('components.nutritionDashboard.dailySummary')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <div className="flex items-center gap-1">
                      <span className="text-sm">{t('components.nutritionDashboard.bmr.title')}</span>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted">
                            <Info className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>{t('components.nutritionDashboard.bmr.dialogTitle')}</DialogTitle>
                            <DialogDescription className="space-y-3 text-sm pt-2">
                              <p>{t('components.nutritionDashboard.bmr.intro')}</p>
                              <div className="bg-muted/50 p-3 rounded-md space-y-2">
                                <p>{t('components.nutritionDashboard.bmr.men')}</p>
                                <p>{t('components.nutritionDashboard.bmr.women')}</p>
                              </div>
                              {profile?.weight_kg && profile?.height_cm && profile?.birth_date && (
                                <p className="text-muted-foreground italic">
                                  {t('components.nutritionDashboard.bmr.summary')}
                                </p>
                              )}
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <span className="text-sm font-medium">{bmr} cal</span>
                  </div>
                  <div className="flex justify-between">
                    <div className="flex items-center gap-1">
                      <span className="text-sm">{t('components.nutritionDashboard.tdee.title')}</span>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted">
                            <Info className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>{t('components.nutritionDashboard.tdee.dialogTitle')}</DialogTitle>
                            <DialogDescription className="space-y-3 text-sm pt-2">
                              <p>{t('components.nutritionDashboard.tdee.intro')}</p>
                              <div className="bg-muted/50 p-3 rounded-md">
                                <ul className="space-y-1 text-xs">
                                  <li>{t('components.nutritionDashboard.tdee.sedentary')}</li>
                                  <li>{t('components.nutritionDashboard.tdee.light')}</li>
                                  <li>{t('components.nutritionDashboard.tdee.moderate')}</li>
                                  <li>{t('components.nutritionDashboard.tdee.active')}</li>
                                  <li>{t('components.nutritionDashboard.tdee.extreme')}</li>
                                </ul>
                              </div>
                              {(nutritionGoals?.activity_level || profile?.activity_level) && (
                                <p className="text-muted-foreground italic">
                                  {`${tdeeForDisplay} cal = ${bmr} × ${nutritionGoals?.activity_level || profile?.activity_level}`}
                                </p>
                              )}
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <span className="text-sm font-medium">{tdeeForDisplay} cal</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-sm">{t('components.nutritionDashboard.calorieGoal.title')}</span>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted">
                            <Info className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>{t('components.nutritionDashboard.calorieGoal.dialogTitle')}</DialogTitle>
                            <DialogDescription className="space-y-3 text-sm pt-2">
                              <p>{t('components.nutritionDashboard.calorieGoal.intro')}</p>
                              <div className="bg-muted/50 p-3 rounded-md">
                                <ul className="space-y-1 text-xs">
                                  <li>{t('components.nutritionDashboard.calorieGoal.loseWeight')}</li>
                                  <li>{t('components.nutritionDashboard.calorieGoal.buildMuscle')}</li>
                                  <li>{t('components.nutritionDashboard.calorieGoal.maintain')}</li>
                                </ul>
                              </div>
                              <p className="text-muted-foreground italic">
                                {nutritionGoals?.goal_type ? nutritionGoals.goal_type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : t('components.nutritionDashboard.calorieGoal.fallback')}
                              </p>
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                      <button
                        onClick={() => {
                          setTempCalorieGoal(String(nutritionGoals?.daily_calories || tdeeForDisplay));
                          setEditingCalorieGoal(true);
                        }}
                        className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted"
                      >
                        <Edit3 className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                      </button>
                    </div>
                    {editingCalorieGoal ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          inputMode="numeric"
                          value={tempCalorieGoal}
                          onChange={(e) => setTempCalorieGoal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveCalorieGoal();
                            if (e.key === 'Escape') setEditingCalorieGoal(false);
                          }}
                          onBlur={handleSaveCalorieGoal}
                          className="w-20 h-7 text-sm text-right"
                          autoFocus
                        />
                        <span className="text-sm text-muted-foreground">cal</span>
                      </div>
                    ) : (
                      <span className="text-sm font-medium">{nutritionGoals?.daily_calories || tdeeForDisplay} cal</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-sm">{t('components.nutritionDashboard.proteinGoal.title')}</span>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted">
                            <Info className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>{t('components.nutritionDashboard.proteinGoal.dialogTitle')}</DialogTitle>
                            <DialogDescription className="space-y-3 text-sm pt-2">
                              <p>{t('components.nutritionDashboard.proteinGoal.intro')}</p>
                              <ul className="list-disc pl-5 space-y-1">
                                <li>{t('components.nutritionDashboard.proteinGoal.loseWeight')}</li>
                                <li>{t('components.nutritionDashboard.proteinGoal.buildMuscle')}</li>
                                <li>{t('components.nutritionDashboard.proteinGoal.maintain')}</li>
                              </ul>
                              <p className="text-muted-foreground italic">
                                {t('components.nutritionDashboard.proteinGoal.summary')}
                              </p>
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                      <button
                        onClick={() => {
                          setTempProteinGoal(String(nutritionGoals?.daily_protein || 120));
                          setEditingProteinGoal(true);
                        }}
                        className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted"
                      >
                        <Edit3 className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                      </button>
                    </div>
                    {editingProteinGoal ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          inputMode="numeric"
                          value={tempProteinGoal}
                          onChange={(e) => setTempProteinGoal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveProteinGoal();
                            if (e.key === 'Escape') setEditingProteinGoal(false);
                          }}
                          onBlur={handleSaveProteinGoal}
                          className="w-16 h-7 text-sm text-right"
                          autoFocus
                        />
                        <span className="text-sm text-muted-foreground">g</span>
                      </div>
                    ) : (
                      <span className="text-sm font-medium">{nutritionGoals?.daily_protein || 120}g</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-sm">{t('components.nutritionDashboard.carbGoal.title')}</span>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted">
                            <Info className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>{t('components.nutritionDashboard.carbGoal.dialogTitle')}</DialogTitle>
                            <DialogDescription className="space-y-3 text-sm pt-2">
                              <p>{t('components.nutritionDashboard.carbGoal.intro')}</p>
                              <p className="text-muted-foreground italic">
                                {t('components.nutritionDashboard.carbGoal.summary')}
                              </p>
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                      <button
                        onClick={() => {
                          setTempCarbGoal(String(nutritionGoals?.daily_carbs || 250));
                          setEditingCarbGoal(true);
                        }}
                        className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted"
                      >
                        <Edit3 className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                      </button>
                    </div>
                    {editingCarbGoal ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          inputMode="numeric"
                          value={tempCarbGoal}
                          onChange={(e) => setTempCarbGoal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveCarbGoal();
                            if (e.key === 'Escape') setEditingCarbGoal(false);
                          }}
                          onBlur={handleSaveCarbGoal}
                          className="w-16 h-7 text-sm text-right"
                          autoFocus
                        />
                        <span className="text-sm text-muted-foreground">g</span>
                      </div>
                    ) : (
                      <span className="text-sm font-medium">{nutritionGoals?.daily_carbs || 250}g</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-sm">{t('components.nutritionDashboard.fatGoal.title')}</span>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted">
                            <Info className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>{t('components.nutritionDashboard.fatGoal.dialogTitle')}</DialogTitle>
                            <DialogDescription className="space-y-3 text-sm pt-2">
                              <p>{t('components.nutritionDashboard.fatGoal.intro')}</p>
                              <p className="text-muted-foreground italic">
                                {t('components.nutritionDashboard.fatGoal.summary')}
                              </p>
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                      <button
                        onClick={() => {
                          setTempFatGoal(String(nutritionGoals?.daily_fat || 65));
                          setEditingFatGoal(true);
                        }}
                        className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted"
                      >
                        <Edit3 className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                      </button>
                    </div>
                    {editingFatGoal ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          inputMode="numeric"
                          value={tempFatGoal}
                          onChange={(e) => setTempFatGoal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveFatGoal();
                            if (e.key === 'Escape') setEditingFatGoal(false);
                          }}
                          onBlur={handleSaveFatGoal}
                          className="w-16 h-7 text-sm text-right"
                          autoFocus
                        />
                        <span className="text-sm text-muted-foreground">g</span>
                      </div>
                    ) : (
                      <span className="text-sm font-medium">{nutritionGoals?.daily_fat || 65}g</span>
                    )}
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="text-xs font-semibold mb-2 text-muted-foreground">{t('components.nutritionDashboard.consumedToday')}</div>
                    <NutritionIcons
                      calories={dailyNutrition?.totals.calories || 0}
                      protein={dailyNutrition?.totals.protein || 0}
                      carbs={dailyNutrition?.totals.carbs || 0}
                      fat={dailyNutrition?.totals.fat || 0}
                      className="flex-col items-start gap-1.5"
                      iconSize={14}
                    />
                  </div>
                  <div className="flex justify-between font-medium border-t pt-2 mt-2">
                    <span className="text-sm">{t('components.nutritionDashboard.remaining')}</span>
                    <span className={`text-sm ${(nutritionGoals?.daily_calories || tdeeForDisplay) - (dailyNutrition?.totals.calories || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(nutritionGoals?.daily_calories || tdeeForDisplay) - (dailyNutrition?.totals.calories || 0)} cal
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vital Information */}
            <VitalStatsCard />

            {/* Nutrition Trends Chart */}
            <NutritionTrendsChart />

            {/* Calorie surplus/deficit (Apple Health only) */}
            <CalorieSurplusDeficitChart />
          </div>
        </div>
      </div>

      <FoodSearchModal
        isOpen={foodSearchModal.isOpen}
        onClose={() => setFoodSearchModal({ ...foodSearchModal, isOpen: false })}
        onSelectFood={handleAddFood}
        mealType={foodSearchModal.mealType}
      />

      <EditFoodEntryModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, entry: null })}
        entry={editModal.entry}
        onSave={handleSaveEdit}
      />

      <PhotoScanModal
        open={photoScanModal.isOpen}
        onClose={() => setPhotoScanModal({ ...photoScanModal, isOpen: false })}
        onAnalysisComplete={handlePhotoAnalysisComplete}
        initialSource={photoScanModal.initialSource}
      />

      <WriteFoodModal
        open={writeFoodModal.isOpen}
        onClose={() => setWriteFoodModal({ ...writeFoodModal, isOpen: false })}
        onAnalysisComplete={handleWriteFoodAnalysisComplete}
      />

      <DetectedFoodReviewModal
        open={detectedFoodsModal.isOpen}
        onClose={() => setDetectedFoodsModal({ isOpen: false, foods: [], mealType: 'breakfast' })}
        detectedFoods={detectedFoodsModal.foods}
        onConfirm={handleConfirmDetectedFoods}
        mealType={detectedFoodsModal.mealType}
      />

      <Dialog open={importMealDialog.isOpen} onOpenChange={(open) => setImportMealDialog({ ...importMealDialog, isOpen: open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('components.nutritionDashboard.importSavedTitle')}</DialogTitle>
            <DialogDescription>
              {t('components.nutritionDashboard.importSavedSubtitle', { meal: importMealDialog.mealType })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {savedMealsLoading ? (
              <p className="text-center text-muted-foreground py-8">{t('components.nutritionDashboard.loadingMeals')}</p>
            ) : savedMeals.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t('components.nutritionDashboard.noSavedMeals')}</p>
            ) : (
              savedMeals.map((meal) => (
                <Button
                  key={meal.id}
                  variant="outline"
                  className="w-full justify-start h-auto py-3 px-4"
                  onClick={() => handleImportSavedMeal(meal.id)}
                >
                  <div className="flex flex-col items-start text-left w-full">
                    <span className="font-semibold">{meal.name}</span>
                    {meal.description && (
                      <span className="text-xs text-muted-foreground">{meal.description}</span>
                    )}
                    <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                      <span>{(meal.items ?? []).length} {t('components.nutritionDashboard.itemsSuffix')}</span>
                      <span>•</span>
                      <span>{(meal.items ?? []).reduce((sum, item) => sum + item.calories, 0).toFixed(0)} cal</span>
                    </div>
                  </div>
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};